import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  Chip,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Collapse,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Article as ArticleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Add as AddIcon,
  History as HistoryIcon,
  EditNote as EditNoteIcon,
  ContentCopy as ContentCopyIcon,
  CloudUpload as CloudUploadIcon,
  DeleteOutline as DeleteOutlineIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { articleApi, KnowledgeEntry, WeChatArticleDraftSyncResponse, WeChatArticleHistory } from '../services/api';

type HistorySidebarProps = {
  historyList: WeChatArticleHistory[];
  selectedHistoryId: number | null;
  historyLoading: boolean;
  deletingHistoryId: number | null;
  onNewConversation: () => void;
  onOpenHistory: (item: WeChatArticleHistory) => void;
  onDeleteHistory: (item: WeChatArticleHistory) => void;
};

function localDateKey(value: string): string {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function historyDateLabel(key: string): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (key === localDateKey(today.toISOString())) return '今天';
  if (key === localDateKey(yesterday.toISOString())) return '昨天';
  const date = new Date(`${key}T00:00:00`);
  return date.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric', weekday: 'short' });
}

const HistorySidebar = memo(function HistorySidebar({
  historyList,
  selectedHistoryId,
  historyLoading,
  deletingHistoryId,
  onNewConversation,
  onOpenHistory,
  onDeleteHistory,
}: HistorySidebarProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const groups = useMemo(() => {
    const grouped = new Map<string, WeChatArticleHistory[]>();
    historyList.forEach((item) => {
      const key = localDateKey(item.createdAt);
      grouped.set(key, [...(grouped.get(key) || []), item]);
    });
    return Array.from(grouped.entries());
  }, [historyList]);

  return (
    <Paper sx={{ p: 2, alignSelf: 'start', height: { md: 'calc(100vh - 190px)' }, overflow: 'auto' }}>
      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon fontSize="small" />
        对话历史
      </Typography>
      <Button fullWidth variant="contained" startIcon={<AddIcon />} sx={{ mb: 1.5 }} onClick={onNewConversation}>
        新增对话
      </Button>
      <Divider sx={{ mb: 1 }} />
      <List dense disablePadding>
        <ListItemButton selected={selectedHistoryId === null} onClick={onNewConversation}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <EditNoteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="新建对话" secondary="当前草稿" primaryTypographyProps={{ noWrap: true }} />
        </ListItemButton>
        {groups.map(([dateKey, items]) => (
          <React.Fragment key={dateKey}>
            <ListItemButton
              onClick={() => setCollapsed((prev) => ({ ...prev, [dateKey]: !prev[dateKey] }))}
              sx={{ mt: 0.5, py: 0.5, px: 1 }}
            >
              <ListItemText
                primary={historyDateLabel(dateKey)}
                secondary={`${items.length} 条`}
                primaryTypographyProps={{ variant: 'caption', fontWeight: 700, color: 'text.secondary' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              {collapsed[dateKey] ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
            </ListItemButton>
            <Collapse in={!collapsed[dateKey]} timeout="auto" unmountOnExit>
              <List dense disablePadding>
                {items.map((item) => (
                  <ListItemButton
                    key={item.id}
                    selected={selectedHistoryId === item.id}
                    onClick={() => onOpenHistory(item)}
                    disabled={(historyLoading && selectedHistoryId === item.id) || deletingHistoryId === item.id}
                    sx={{ pl: 2 }}
                  >
                    <ListItemText
                      primary={item.topic}
                      secondary={new Date(item.createdAt).toLocaleTimeString('zh-CN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      primaryTypographyProps={{ noWrap: true }}
                    />
                    <Tooltip title="删除这条记录">
                      <IconButton
                        size="small"
                        aria-label={`删除 ${item.topic}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onDeleteHistory(item);
                        }}
                        disabled={deletingHistoryId === item.id}
                      >
                        {deletingHistoryId === item.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
      {historyList.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          暂无历史记录
        </Typography>
      )}
    </Paper>
  );
});

const TEMPLATES = [
  { value: 'general', label: '通用公众号长文' },
  { value: 'news', label: '新闻通稿' },
  { value: 'event', label: '活动通知' },
  { value: 'policy', label: '政策解读' },
  { value: 'product', label: '产品发布' },
];

type ArticleFormProps = {
  topic: string;
  extra: string;
  template: string;
  loading: boolean;
  error: string | null;
  suggestedTitles: string[];
  onTopicChange: (value: string) => void;
  onExtraChange: (value: string) => void;
  onTemplateChange: (value: string) => void;
  onErrorClose: () => void;
  onGenerate: () => void;
  onSuggestTitles: () => void;
  onSelectTitle: (title: string) => void;
};

const ArticleForm = memo(function ArticleForm({
  topic,
  extra,
  template,
  loading,
  error,
  suggestedTitles,
  onTopicChange,
  onExtraChange,
  onTemplateChange,
  onErrorClose,
  onGenerate,
  onSuggestTitles,
  onSelectTitle,
}: ArticleFormProps) {
  const [titleLoading, setTitleLoading] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationElapsed, setGenerationElapsed] = useState(0);

  useEffect(() => {
    if (!loading) {
      setGenerationProgress(0);
      setGenerationElapsed(0);
      return;
    }
    const startedAt = Date.now();
    const updateProgress = () => {
      const elapsed = (Date.now() - startedAt) / 1000;
      let progress: number;
      if (elapsed < 2) progress = 5 + elapsed * 5;
      else if (elapsed < 4) progress = 15 + (elapsed - 2) * 5;
      else if (elapsed < 7) progress = 25 + (elapsed - 4) * 5;
      else if (elapsed < 18) progress = 40 + (elapsed - 7) * 4;
      else if (elapsed < 35) progress = 84 + (elapsed - 18) * 0.6;
      else progress = Math.min(97, 94 + (elapsed - 35) * 0.08);
      setGenerationElapsed(Math.floor(elapsed));
      setGenerationProgress(Math.round(progress));
    };
    updateProgress();
    const timer = window.setInterval(updateProgress, 500);
    return () => window.clearInterval(timer);
  }, [loading]);

  const generationStage =
    generationProgress < 15
      ? '准备写作素材'
      : generationProgress < 25
        ? '检索知识条目'
        : generationProgress < 40
          ? '规划文章结构'
          : generationProgress < 84
            ? '生成文章正文'
            : '润色与排版';

  const handleSuggest = useCallback(async () => {
    if (!topic.trim()) {
      setTitleError('请填写主题后再获取标题建议');
      return;
    }
    setTitleLoading(true);
    setTitleError(null);
    try {
      await onSuggestTitles();
    } catch (err: any) {
      setTitleError(err?.message || '获取标题失败');
    } finally {
      setTitleLoading(false);
    }
  }, [topic, onSuggestTitles]);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="article-template-label">文章模板</InputLabel>
          <Select
            labelId="article-template-label"
            value={template}
            label="文章模板"
            onChange={(e) => onTemplateChange(e.target.value)}
          >
            {TEMPLATES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="主题 / 核心信息"
          placeholder="例如：介绍我司新上线的知识库能力与使用场景"
          value={topic}
          onChange={(e) => onTopicChange(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          required
        />
        <TextField
          label="额外要求（可选）"
          placeholder="字数、必含要点、不要提及的数据等"
          value={extra}
          onChange={(e) => onExtraChange(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
        {suggestedTitles.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              候选标题（点击使用）
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {suggestedTitles.map((title) => (
                <Chip
                  key={title}
                  label={title}
                  onClick={() => onSelectTitle(title)}
                  clickable
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
        {titleError && (
          <Alert severity="error" onClose={() => setTitleError(null)}>
            {titleError}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={onErrorClose}>
            {error}
          </Alert>
        )}
        {loading && (
          <Paper
            variant="outlined"
            sx={{ p: 2, bgcolor: 'rgba(37, 99, 235, 0.03)', borderColor: 'primary.light' }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {generationStage}
              </Typography>
              <Typography variant="body2" color="primary.main" sx={{ fontWeight: 700 }}>
                预计 {generationProgress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={generationProgress}
              sx={{ height: 8, borderRadius: 999, mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              已等待 {generationElapsed} 秒 · 通常约 15–30 秒，实际时间受素材长度和模型服务影响
            </Typography>
          </Paper>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={onGenerate}
            disabled={loading}
          >
            {loading ? '生成中…' : '根据知识库生成草稿'}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleSuggest}
            disabled={loading || titleLoading}
          >
            {titleLoading ? '标题生成中…' : 'AI 推荐标题'}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
});

type ArticlePreviewProps = {
  article: string;
  sources: KnowledgeEntry[];
};

/**
 * 微信编辑器会丢弃 class 与 <style>，只认元素上的 style 属性，
 * 因此复制前把预览 DOM 的样式逐个内联，粘贴后才能保留排版。
 */
const WECHAT_INLINE_STYLES: Record<string, string> = {
  P: 'margin: 0 0 16px; font-size: 15px; line-height: 1.75; color: #333; letter-spacing: 0.5px;',
  H1: 'margin: 28px 0 16px; font-size: 20px; font-weight: bold; line-height: 1.5; color: #222;',
  H2: 'margin: 26px 0 14px; font-size: 18px; font-weight: bold; line-height: 1.5; color: #222;',
  H3: 'margin: 22px 0 12px; font-size: 16px; font-weight: bold; line-height: 1.5; color: #222;',
  H4: 'margin: 20px 0 12px; font-size: 15px; font-weight: bold; line-height: 1.5; color: #222;',
  UL: 'margin: 0 0 16px; padding-left: 22px;',
  OL: 'margin: 0 0 16px; padding-left: 22px;',
  LI: 'margin-bottom: 8px; font-size: 15px; line-height: 1.75; color: #333;',
  STRONG: 'font-weight: bold; color: #222;',
  EM: 'font-style: italic;',
  BLOCKQUOTE:
    'margin: 0 0 16px; padding: 12px 14px; border-left: 3px solid #2d6cdf; background: #f6f8fb; color: #555; font-size: 14px; line-height: 1.75;',
  CODE: 'padding: 1px 5px; border-radius: 3px; background: #f2f4f7; color: #c7254e; font-size: 14px;',
  PRE: 'margin: 0 0 16px; padding: 12px; border-radius: 4px; background: #f6f8fa; overflow-x: auto; font-size: 13px; line-height: 1.6;',
  HR: 'margin: 24px 0; border: none; border-top: 1px solid #e6e9ef;',
  A: 'color: #2d6cdf; text-decoration: none;',
  TABLE: 'width: 100%; margin: 0 0 16px; border-collapse: collapse; font-size: 14px;',
  TH: 'padding: 8px; border: 1px solid #dfe3ea; background: #f4f6fa; text-align: left;',
  TD: 'padding: 8px; border: 1px solid #dfe3ea;',
  IMG: 'max-width: 100%; height: auto;',
};

function buildWeChatHtml(previewEl: HTMLElement): string {
  const clone = previewEl.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('*').forEach((node) => {
    const el = node as HTMLElement;
    el.removeAttribute('class');
    const style = WECHAT_INLINE_STYLES[el.tagName];
    if (style) {
      el.setAttribute('style', style);
    }
  });
  return `<div style="font-size: 15px; line-height: 1.75; color: #333;">${clone.innerHTML}</div>`;
}

async function copyRichText(html: string, plainText: string): Promise<void> {
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
      }),
    ]);
    return;
  }
  // 旧版浏览器回退：选中一段临时富文本再执行 copy，仍能保留排版
  const holder = document.createElement('div');
  holder.contentEditable = 'true';
  holder.innerHTML = html;
  holder.style.position = 'fixed';
  holder.style.left = '-9999px';
  holder.style.top = '0';
  document.body.appendChild(holder);
  const range = document.createRange();
  range.selectNodeContents(holder);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
  const ok = document.execCommand('copy');
  selection?.removeAllRanges();
  document.body.removeChild(holder);
  if (!ok) {
    throw new Error('复制失败');
  }
}

const ArticlePreview = memo(function ArticlePreview({ article, sources }: ArticlePreviewProps) {
  const [copied, setCopied] = useState<'rich' | 'markdown' | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const flagCopied = useCallback((kind: 'rich' | 'markdown') => {
    setCopyError(null);
    setCopied(kind);
    window.setTimeout(() => setCopied(null), 2000);
  }, []);

  const handleCopyForWeChat = useCallback(async () => {
    const previewEl = previewRef.current;
    if (!article || !previewEl) {
      return;
    }
    try {
      await copyRichText(buildWeChatHtml(previewEl), previewEl.innerText);
      flagCopied('rich');
    } catch {
      setCopyError('富文本复制失败，请改用「复制 Markdown」');
    }
  }, [article, flagCopied]);

  const handleCopyMarkdown = useCallback(async () => {
    if (!article) {
      return;
    }
    try {
      await navigator.clipboard.writeText(article);
      flagCopied('markdown');
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = article;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      flagCopied('markdown');
    }
  }, [article, flagCopied]);

  if (!article && sources.length === 0) {
    return null;
  }

  return (
    <>
      {sources.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            参考知识片段
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {sources.map((s) => (
              <Chip
                key={s.id}
                size="small"
                label={s.document?.title ? `${s.document.title} (#${s.id})` : `条目 #${s.id}`}
              />
            ))}
          </Box>
        </Paper>
      )}
      {article && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
            <Typography variant="h6">草稿预览</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyForWeChat}
              >
                {copied === 'rich' ? '已复制，可直接粘贴' : '复制到公众号'}
              </Button>
              <Button variant="outlined" size="small" onClick={handleCopyMarkdown}>
                {copied === 'markdown' ? '已复制' : '复制 Markdown'}
              </Button>
            </Stack>
          </Box>
          {copyError && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCopyError(null)}>
              {copyError}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            「复制到公众号」会带上排版样式，粘贴进公众平台编辑器即可，图片与超链接仍需在微信后台补充。
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box
            ref={previewRef}
            className="wechat-article-md"
            sx={{
              '& h1, & h2, & h3': { mt: 2, mb: 1 },
              '& p': { mb: 1.5, lineHeight: 1.75 },
              '& ul': { pl: 2, mb: 1.5 },
            }}
          >
            <ReactMarkdown>{article}</ReactMarkdown>
          </Box>
        </Paper>
      )}
    </>
  );
});

type DraftSyncPanelProps = {
  historyId: number;
  defaultTitle: string;
  draftStatus?: string;
  draftSyncedAt?: string;
  draftError?: string;
  onSynced: (result: WeChatArticleDraftSyncResponse) => void;
};

const DraftSyncPanel = memo(function DraftSyncPanel({
  historyId,
  defaultTitle,
  draftStatus,
  draftSyncedAt,
  draftError,
  onSynced,
}: DraftSyncPanelProps) {
  const [title, setTitle] = useState(defaultTitle);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(defaultTitle);
  }, [defaultTitle, historyId]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setError(null);
    setMessage(null);
    try {
      const formData = new FormData();
      if (title.trim()) {
        formData.append('title', title.trim());
      }
      if (coverFile) {
        formData.append('coverImage', coverFile);
      }
      const result = await articleApi.syncDraftToWeChat(historyId, formData);
      setMessage(result.message);
      onSynced(result);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '同步失败';
      setError(typeof msg === 'string' ? msg : '同步失败');
    } finally {
      setSyncing(false);
    }
  }, [historyId, title, coverFile, onSynced]);

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUploadIcon color="primary" fontSize="small" />
        同步到公众号草稿箱
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        将 Markdown 转为图文草稿并写入微信公众平台草稿箱；请在 mp.weixin.qq.com 审校后手动发布。
      </Typography>
      {draftStatus === 'SYNCED' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          已同步{draftSyncedAt ? `（${new Date(draftSyncedAt).toLocaleString()}）` : ''}，请登录公众平台草稿箱继续编辑发布。
        </Alert>
      )}
      {draftStatus === 'FAILED' && draftError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          上次同步失败：{draftError}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Stack spacing={2}>
        <TextField
          label="文章标题（同步到微信，最多 64 字）"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
        />
        <Box>
          <Button variant="outlined" component="label" size="small">
            {coverFile ? `封面：${coverFile.name}` : '选择封面图（JPG/PNG，建议 900×500）'}
            <input
              type="file"
              hidden
              accept="image/jpeg,image/png,image/jpg"
              onChange={(e) => setCoverFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            同步前请上传封面图，或在服务端配置 WECHAT_MP_DEFAULT_COVER_MEDIA_ID；并开启 WECHAT_MP_ENABLED=true。
          </Typography>
        </Box>
        <Box>
          <Button
            variant="contained"
            startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? '同步中…' : '同步到草稿箱'}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
});

const WeChatArticle: React.FC = () => {
  const [topic, setTopic] = useState('');
  const [extra, setExtra] = useState('');
  const [template, setTemplate] = useState('general');
  const [suggestedTitles, setSuggestedTitles] = useState<string[]>([]);
  const [article, setArticle] = useState('');
  const [sources, setSources] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deletingHistoryId, setDeletingHistoryId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [historyList, setHistoryList] = useState<WeChatArticleHistory[]>([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);
  const [draftMeta, setDraftMeta] = useState<{
    status?: string;
    syncedAt?: string;
    error?: string;
  }>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await articleApi.getHistoryList();
        if (!cancelled) {
          setHistoryList(list || []);
        }
      } catch (e) {
        console.error('加载历史记录失败', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const list = await articleApi.getHistoryList();
      setHistoryList(list || []);
    } catch (e) {
      console.error('加载历史记录失败', e);
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!topic.trim()) {
      setError('请填写文章主题或要点');
      return;
    }
    setLoading(true);
    setError(null);
    setArticle('');
    setSources([]);
    try {
      const data = await articleApi.generateWeChatArticle({
        topic: topic.trim(),
        extraInstructions: extra.trim() || undefined,
        maxResults: 8,
        template: template || 'general',
        baseHistoryId: selectedHistoryId ?? undefined,
      });
      setArticle(data.article || '');
      setSources(data.sourcesUsed || []);
      setSelectedHistoryId(data.historyId ?? null);
      setDraftMeta({});
      await loadHistory();
    } catch (err: any) {
      const d = err?.response?.data;
      const msg =
        (typeof d === 'object' && d?.message) ||
        (typeof d === 'string' ? d : null) ||
        err?.message ||
        '生成失败，请稍后重试';
      setError(typeof msg === 'string' ? msg : '生成失败');
    } finally {
      setLoading(false);
    }
  }, [topic, extra, template, selectedHistoryId, loadHistory]);

  const handleSuggestTitles = useCallback(async () => {
    const data = await articleApi.suggestTitles({
      topic: topic.trim(),
    });
    setSuggestedTitles(data.titles || []);
  }, [topic]);

  const handleSelectTitle = useCallback((title: string) => {
    setTopic(title);
  }, []);

  const handleOpenHistory = useCallback(async (item: WeChatArticleHistory) => {
    setSelectedHistoryId(item.id);
    setTopic(item.topic || '');
    setExtra(item.extraInstructions || '');
    setTemplate(item.template || 'general');
    setSuggestedTitles([]);
    setHistoryLoading(true);
    try {
      const detail = await articleApi.getHistoryDetail(item.id);
      setArticle(detail.article || '');
      setSources(detail.sourcesUsed || []);
      setDraftMeta({
        status: item.wechatDraftStatus,
        syncedAt: item.wechatDraftSyncedAt,
        error: item.wechatDraftError,
      });
      setError(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '加载历史详情失败';
      setError(msg);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleNewConversation = useCallback(() => {
    setSelectedHistoryId(null);
    setTopic('');
    setExtra('');
    setTemplate('general');
    setSuggestedTitles([]);
    setArticle('');
    setSources([]);
    setDraftMeta({});
    setError(null);
  }, []);

  const handleDeleteHistory = useCallback(
    async (item: WeChatArticleHistory) => {
      if (!window.confirm(`确定删除“${item.topic}”吗？删除后无法恢复。`)) {
        return;
      }
      setDeletingHistoryId(item.id);
      try {
        await articleApi.deleteHistory(item.id);
        setHistoryList((prev) => prev.filter((history) => history.id !== item.id));
        if (selectedHistoryId === item.id) {
          handleNewConversation();
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || '删除历史记录失败');
      } finally {
        setDeletingHistoryId(null);
      }
    },
    [handleNewConversation, selectedHistoryId]
  );

  const handleDraftSynced = useCallback(
    (result: WeChatArticleDraftSyncResponse) => {
      setDraftMeta({
        status: result.draftStatus,
        syncedAt: result.syncedAt,
        error: undefined,
      });
      setHistoryList((prev) =>
        prev.map((item) =>
          item.id === result.historyId
            ? {
                ...item,
                wechatDraftStatus: result.draftStatus,
                wechatDraftTitle: result.title,
                wechatDraftMediaId: result.mediaId,
                wechatDraftSyncedAt: result.syncedAt,
                wechatDraftError: undefined,
              }
            : item
        )
      );
    },
    []
  );

  const activeConversationTitle =
    selectedHistoryId != null
      ? historyList.find((item) => item.id === selectedHistoryId)?.topic || '历史会话'
      : '新建对话';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
        gap: 2,
        minHeight: { md: 'calc(100vh - 180px)' },
      }}
    >
      <HistorySidebar
        historyList={historyList}
        selectedHistoryId={selectedHistoryId}
        historyLoading={historyLoading}
        deletingHistoryId={deletingHistoryId}
        onNewConversation={handleNewConversation}
        onOpenHistory={handleOpenHistory}
        onDeleteHistory={handleDeleteHistory}
      />

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon color="primary" />
          公众号文章生成
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          根据企业知识库全库自动检索并生成草稿，语气与读者画像由系统默认设定。
        </Typography>
        <Chip label={`当前会话：${activeConversationTitle}`} sx={{ mb: 2 }} />

        <ArticleForm
          topic={topic}
          extra={extra}
          template={template}
          loading={loading}
          error={error}
          suggestedTitles={suggestedTitles}
          onTopicChange={setTopic}
          onExtraChange={setExtra}
          onTemplateChange={setTemplate}
          onErrorClose={() => setError(null)}
          onGenerate={handleGenerate}
          onSuggestTitles={handleSuggestTitles}
          onSelectTitle={handleSelectTitle}
        />

        {historyLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!historyLoading && <ArticlePreview article={article} sources={sources} />}

        {!historyLoading && selectedHistoryId != null && article && (
          <DraftSyncPanel
            historyId={selectedHistoryId}
            defaultTitle={topic.trim() || '未命名文章'}
            draftStatus={draftMeta.status}
            draftSyncedAt={draftMeta.syncedAt}
            draftError={draftMeta.error}
            onSynced={handleDraftSynced}
          />
        )}
      </Box>
    </Box>
  );
};

export default WeChatArticle;
